import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SESSION_STATUS_LABEL, type SessionStatus } from '@doit/shared';
import { CalendarDays, ChevronLeft, ChevronRight, Clock3, Pencil, Plus, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Segmented, Select } from '../../components/form';
import { Badge, DirectionTag } from '../../components/badges';
import { useCurrentUser } from '../../api/auth';
import { useGroups, useSessions } from '../../api/queries';
import type { Session } from '../../api/types';
import { formatDateLong, toIsoDate } from '../../lib/format';
import { ExtraSessionModal } from './ExtraSessionModal';
import { SessionEditModal } from './SessionEditModal';

const STATUS_TONE: Record<SessionStatus, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  SCHEDULED: 'neutral',
  HELD: 'ok',
  POSTPONED: 'warn',
  CANCELLED: 'danger',
};

/** დღის უჯრაში ფერი — სტატუსი ერთი შეხედვით უნდა იკითხებოდეს */
const STATUS_DOT: Record<SessionStatus, string> = {
  SCHEDULED: 'bg-brand-600',
  HELD: 'bg-ok',
  POSTPONED: 'bg-warn',
  CANCELLED: 'bg-danger',
};

const WEEKDAY_HEAD = ['ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი'];

type View = 'month' | 'week' | 'list';

function toKey(d: Date): string {
  return toIsoDate(d);
}

/** კვირის 7 დღე — ორშაბათიდან */
function weekGrid(anchor: Date): Date[] {
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** თვის ბადე — ორშაბათიდან, სრული კვირებით */
function monthGrid(anchor: Date): Date[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  // JS: 0=კვირა → ბადე ორშაბათიდან იწყება
  const shift = (first.getDay() + 6) % 7;
  start.setDate(first.getDate() - shift);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

const MONTHS = [
  'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
  'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი',
];

export function SchedulePage() {
  const user = useCurrentUser();
  const isStaff = user.role !== 'LECTURER';

  // ლექტორის სამუშაო ჭრილი კვირაა; მენეჯერი მთელ თვეს გეგმავს
  const [view, setView] = useState<View>(isStaff ? 'month' : 'week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [groupId, setGroupId] = useState('');
  const [selected, setSelected] = useState<string | null>(toKey(new Date()));
  const [extraOpen, setExtraOpen] = useState(false);
  const [editing, setEditing] = useState<Session | null>(null);

  const { data: groups } = useGroups({ pageSize: 100 });

  const grid = useMemo(
    () => (view === 'week' ? weekGrid(anchor) : monthGrid(anchor)),
    [anchor, view],
  );

  const bounds = useMemo(
    () => ({ from: toKey(grid[0]!), to: toKey(grid[grid.length - 1]!) }),
    [grid],
  );

  const { data: sessions, isLoading } = useSessions({
    from: bounds.from,
    to: bounds.to,
    groupId: groupId || undefined,
    limit: 500,
  });

  const byDate = useMemo(() => {
    const map = new Map<string, Session[]>();
    for (const s of sessions ?? []) {
      const key = s.date.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return map;
  }, [sessions]);

  const todayKey = toKey(new Date());
  const daySessions = selected ? (byDate.get(selected) ?? []) : [];

  function shiftPeriod(delta: number) {
    setAnchor((prev) => {
      if (view === 'week') {
        const next = new Date(prev);
        next.setDate(prev.getDate() + delta * 7);
        return next;
      }
      return new Date(prev.getFullYear(), prev.getMonth() + delta, 1);
    });
  }

  /** სათაურის პერიოდი: თვე ან კვირის შუალედი */
  const periodTitle =
    view === 'week'
      ? (() => {
          const [a, b] = [grid[0]!, grid[6]!];
          return a.getMonth() === b.getMonth()
            ? `${a.getDate()}–${b.getDate()} ${MONTHS[a.getMonth()]} ${b.getFullYear()}`
            : `${a.getDate()} ${MONTHS[a.getMonth()]} – ${b.getDate()} ${MONTHS[b.getMonth()]} ${b.getFullYear()}`;
        })()
      : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;

  return (
    <>
      <PageHeader
        title="განრიგი"
        subtitle={isStaff ? 'ყველა ჯგუფის გაკვეთილები' : undefined}
        action={
          isStaff ? (
            <Button icon={<Plus className="size-4" />} onClick={() => setExtraOpen(true)}>
              დამატებითი ლექცია
            </Button>
          ) : undefined
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'month', label: 'თვე' },
            { value: 'week', label: 'კვირა' },
            // ლექტორს მხოლოდ კალენდრის ინტერფეისი აქვს
            ...(isStaff ? [{ value: 'list' as const, label: 'სია' }] : []),
          ]}
        />
        <Select
          className="max-w-64"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          placeholder="ყველა ჯგუფი"
          options={(groups?.items ?? []).map((g) => ({ value: g.id, label: g.name }))}
        />

        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => shiftPeriod(-1)}
            aria-label={view === 'week' ? 'წინა კვირა' : 'წინა თვე'}
            className="rounded-md border border-line-strong p-1.5 text-ink-soft transition hover:border-brand-600 hover:text-brand-700"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="min-w-40 text-center text-[13px] font-medium text-ink">
            {periodTitle}
          </span>
          <button
            onClick={() => shiftPeriod(1)}
            aria-label={view === 'week' ? 'შემდეგი კვირა' : 'შემდეგი თვე'}
            className="rounded-md border border-line-strong p-1.5 text-ink-soft transition hover:border-brand-600 hover:text-brand-700"
          >
            <ChevronRight className="size-4" />
          </button>
          <button
            onClick={() => {
              setAnchor(new Date());
              setSelected(todayKey);
            }}
            className="ml-1 rounded-md border border-line-strong px-2.5 py-1.5 text-[12px] text-ink-soft transition hover:border-brand-600 hover:text-brand-700"
          >
            დღეს
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : view !== 'list' ? (
        <div className="grid gap-5 lg:grid-cols-3">
          {/* --- ბადე --- */}
          <div className="overflow-hidden rounded-[10px] border border-line bg-surface shadow-xs lg:col-span-2">
            <div className="grid grid-cols-7 border-b border-line bg-canvas/70">
              {WEEKDAY_HEAD.map((d) => (
                <span
                  key={d}
                  className="px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-ink-mute"
                >
                  {d}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {grid.map((day) => {
                const key = toKey(day);
                const list = byDate.get(key) ?? [];
                // კვირის ხედში ყველა დღე „ამ პერიოდისაა"
                const otherMonth = view === 'month' && day.getMonth() !== anchor.getMonth();
                const isToday = key === todayKey;
                const isSelected = key === selected;

                return (
                  <button
                    key={key}
                    onClick={() => setSelected(key)}
                    className={clsx(
                      'border-b border-r border-line p-1.5 text-left align-top transition',
                      view === 'week' ? 'min-h-44' : 'min-h-20',
                      otherMonth && 'bg-canvas/40 text-ink-mute',
                      isSelected ? 'ring-2 ring-inset ring-brand-600' : 'hover:bg-canvas/60',
                    )}
                  >
                    <span
                      className={clsx(
                        'tabular inline-flex size-6 items-center justify-center rounded-full text-[12px]',
                        isToday
                          ? 'bg-brand-600 font-semibold text-white'
                          : otherMonth
                            ? 'text-ink-mute'
                            : 'text-ink',
                      )}
                    >
                      {day.getDate()}
                    </span>

                    <span className="mt-1 flex flex-col gap-0.5">
                      {list.slice(0, view === 'week' ? 8 : 3).map((s) => (
                        <span
                          key={s.id}
                          className="flex items-center gap-1 truncate text-[10px] text-ink-soft"
                          title={`${s.startTime} ${s.group.name}`}
                        >
                          <span
                            className={clsx('size-1.5 shrink-0 rounded-full', STATUS_DOT[s.status])}
                          />
                          <span className="tabular truncate">
                            {s.startTime} {s.group.name}
                          </span>
                          {s.isMakeup && <RotateCcw className="size-2.5 shrink-0 text-warn" />}
                        </span>
                      ))}
                      {list.length > (view === 'week' ? 8 : 3) && (
                        <span className="text-[10px] text-ink-mute">
                          +{list.length - (view === 'week' ? 8 : 3)}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* --- არჩეული დღე --- */}
          <div className="rounded-[10px] border border-line bg-surface p-4 shadow-xs">
            <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-ink">
              {selected ? formatDateLong(selected) : 'აირჩიე დღე'}
              {selected === todayKey && (
                <span className="rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium uppercase text-white">
                  დღეს
                </span>
              )}
            </h2>

            {daySessions.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-ink-mute">
                ამ დღეს გაკვეთილი არ არის
              </p>
            ) : (
              <ul className="space-y-2">
                {daySessions.map((s) => (
                  <li key={s.id} className="flex items-stretch gap-1.5">
                    <div className="min-w-0 flex-1">
                      <SessionCard session={s} />
                    </div>
                    {isStaff && (
                      <button
                        onClick={() => setEditing(s)}
                        aria-label="ამ დღის ცვლილება"
                        title="ამ დღის ცვლილება"
                        className="shrink-0 rounded-lg border border-line px-2 text-ink-mute transition hover:border-brand-600 hover:text-brand-700"
                      >
                        <Pencil className="size-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        /* --- სიის ხედი --- */
        <div className="space-y-5">
          {[...byDate.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, list]) => (
              <section key={date}>
                <h2 className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-ink">
                  {formatDateLong(date)}
                  {date === todayKey && (
                    <span className="rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium uppercase text-white">
                      დღეს
                    </span>
                  )}
                  <span className="text-[12px] font-normal text-ink-mute">
                    · {list.length} გაკვეთილი
                  </span>
                </h2>
                <ul className="space-y-2">
                  {list.map((s) => (
                    <li key={s.id}>
                      <SessionCard session={s} wide />
                    </li>
                  ))}
                </ul>
              </section>
            ))}

          {byDate.size === 0 && (
            <div className="rounded-[10px] border border-line bg-surface shadow-xs">
              <EmptyState
                icon={<CalendarDays className="size-8" />}
                title="ამ თვეში გაკვეთილები არ არის"
                description="შეცვალე თვე ან ჯგუფის განრიგში დააგენერირე გაკვეთილები."
              />
            </div>
          )}
        </div>
      )}

      {isStaff && <SessionEditModal session={editing} onClose={() => setEditing(null)} />}

      {isStaff && (
        <ExtraSessionModal
          open={extraOpen}
          onClose={() => setExtraOpen(false)}
          groups={groups?.items ?? []}
          defaultDate={selected ?? todayKey}
          defaultGroupId={groupId}
        />
      )}
    </>
  );
}

// ============================================================

function SessionCard({ session: s, wide = false }: { session: Session; wide?: boolean }) {
  return (
    <Link
      to={`/journal/${s.id}`}
      className={clsx(
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-line px-3 py-2.5 transition hover:border-brand-600 hover:bg-canvas/60',
        wide && 'bg-surface',
      )}
    >
      <span className="tabular w-12 shrink-0 text-[13px] font-medium text-ink">{s.startTime}</span>
      <span className="tabular shrink-0 text-[13px] font-semibold text-brand-700">
        {s.group.name}
      </span>
      {wide && (
        <DirectionTag
          name={s.group.course.direction.name}
          color={s.group.course.direction.color}
        />
      )}
      <span className="min-w-0 flex-1 truncate text-[12px] text-ink-soft">
        {s.topic ?? s.group.course.name}
      </span>
      {s.isMakeup && (
        <Badge tone="warn">
          <RotateCcw className="mr-0.5 inline size-2.5" />
          აღდგენა
        </Badge>
      )}
      {s.timeOverride && (
        <Badge tone="brand">
          <Clock3 className="mr-0.5 inline size-2.5" />
          შეცვლილი დრო
        </Badge>
      )}
      <Badge tone={STATUS_TONE[s.status]}>{SESSION_STATUS_LABEL[s.status]}</Badge>
    </Link>
  );
}
